import React, { useMemo } from "react";
import {
  Button,
  Typography,
  Box,
  Divider,
  Snackbar,
  Alert,
  CircularProgress
} from "@mui/material";
import { CloudUpload, Save } from "@mui/icons-material";
import { styled } from '@mui/material/styles';
import ImageOrderList from "./ImageOrderList"; // Make sure this component is also updated
import { useUploadAsset } from "../../hooks/asset/useUploadAsset"; // The hook we created
import { useAsset } from "../../context/AssetContext";
import { UploadAssetContext } from "../../context/UploadAssetContext";

interface UploadAssetProps {
  isEmbedded?: boolean;
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadAsset: React.FC<UploadAssetProps> = ({ isEmbedded = false }) => {
  const { asset: ogAsset, updateAssetMedia, closeEditAssetModal, refetchAsset } = useAsset();

  if (!ogAsset) return null;

  const handleSaveSuccess = () => {
    refetchAsset(); // First, tell the page to refetch its data
    closeEditAssetModal(); // Then, close the modal
  };

  const uploadState = useUploadAsset({
    initialAsset: ogAsset,
    onSave: updateAssetMedia,
    onSaveSuccess: handleSaveSuccess
  });

  const {
    asset: editingAsset, // alias for clarity
    uploadedFiles,
    currentImageType,
    isSaving,
    notification,
    handleFileUpload,
    handleCloseNotification,
    handleSave,
  } = uploadState;

  const hasChanges = useMemo(() => {
    if (uploadedFiles.length > 0) return true;
    return JSON.stringify(ogAsset) !== JSON.stringify(editingAsset);
  }, [ogAsset, editingAsset, uploadedFiles]);

  return (
    <UploadAssetContext.Provider value={uploadState}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '75vh' }}>
        <Box sx={{ flexShrink: 0, p: 2, pb: 0 }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between', // Pushes items to opposite ends
            alignItems: 'center',            // Vertically aligns them
            mb: 2                            // Margin bottom before the divider
          }}>
            <Typography variant="subtitle1" color="text.secondary">
              {editingAsset.assetIdentifier}
            </Typography>
            <Button component="label" variant="contained" startIcon={<CloudUpload />} size="large">
              Upload {currentImageType.replace('image', '')}
              <VisuallyHiddenInput type="file" onChange={handleFileUpload} accept="image/*,.heic,.heif" multiple />
            </Button>
          </Box>

          {/* The Divider now comes after the new header row */}
          <Divider sx={{mb: 5}} />

        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2 }}>
          <ImageOrderList />
        </Box>
        {!isEmbedded && (
          <Box sx={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            p: 2,
            mt: 0,
            borderTop: '1px solid rgba(255, 255, 255, 0.12)'
          }}>
            <Button variant="outlined" color="secondary" onClick={closeEditAssetModal}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <Save />}
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        )}
      </Box>
      <Snackbar open={notification.open} autoHideDuration={6000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </UploadAssetContext.Provider>
  );
};

export default UploadAsset;